/**
 * ChartShowcase — ECharts 4종 핵심 차트(Line/Bar/Pie/Radar) 카탈로그.
 * (상세 JSDoc 헤더는 본 파일 하단 export default 함수 위 참조)
 */
import { Card, Typography } from 'antd'
import ReactECharts from 'echarts-for-react'
import PageContainer from '@/components/common/PageContainer'
import { useLabel } from '@/framework/useLabel'

const { Title } = Typography

// ─── 차트 옵션 #1: Line (선그래프) ─
// Why: 시계열 추세 표현 — 일자/시간별 KPI 변동을 가장 직관적으로 시연.
// When: 연속형 X축(날짜/시간), 변동 패턴 강조. 카테고리 비교는 Bar 사용.
// Pitfall: smooth=true 는 곡선 보간 — 실제 데이터 점이 모호해짐 (정확값 강조 시 false).
const line = {
  title: { text: 'Line Chart', left: 'center', textStyle: { fontSize: 13 } },
  // grid: 차트 본체 여백 (top=제목 높이, bottom=X축 라벨 영역 등)
  grid: { top: 36, bottom: 28, left: 40, right: 16 },
  xAxis: { type: 'category', data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] },
  yAxis: { type: 'value' },
  // smooth=true 곡선 보간 — Line 의 시각적 부드러움 옵션
  series: [{ type: 'line', smooth: true, data: [120, 200, 150, 80, 170] }]
}

// ─── 차트 옵션 #2: Bar (막대그래프) ─
// Why: 카테고리간 단순 비교 — 절대값 비교/순위에 최적.
// When: 이산형 X축(상품/지역명 등). 순위 강조시 sort.
// Pitfall: 카테고리 N>10 이면 가독성 저하 — 가로 막대(yAxis category) 또는 Top N 검토.
const bar = {
  title: { text: 'Bar Chart', left: 'center', textStyle: { fontSize: 13 } },
  grid: { top: 36, bottom: 28, left: 40, right: 16 },
  xAxis: { type: 'category', data: ['A', 'B', 'C', 'D', 'E'] },
  yAxis: { type: 'value' },
  series: [{ type: 'bar', data: [50, 120, 80, 200, 90] }]
}

// ─── 차트 옵션 #3: Pie / Donut (원그래프) ─
// Why: 전체 대비 부분 비율 표현 (총합 100%).
// When: 카테고리 ≤6, 비율차이 ≥5% 일 때만 권장. 순위는 Bar 가 정확.
// Pitfall: radius 배열 = [내경, 외경] (도넛). 단일 값이면 솔리드 파이.
const pie = {
  title: { text: 'Pie Chart', left: 'center', textStyle: { fontSize: 13 } },
  tooltip: { trigger: 'item' },
  series: [
    {
      type: 'pie',
      radius: ['35%', '65%'], // 도넛 형태 (내경 35%, 외경 65%)
      center: ['50%', '55%'], // 중심 좌표 (제목 영역 보정)
      data: [
        { value: 335, name: 'A' },
        { value: 310, name: 'B' },
        { value: 234, name: 'C' },
        { value: 135, name: 'D' }
      ]
    }
  ]
}

// ─── 차트 옵션 #4: Radar (방사형) ─
// Why: 다차원 지표 (3~7축) 의 균형/편향을 한 그림에 표현.
// When: 조직별 역량 평가, 상품 속성 비교 등 다축 비교 (5~7축 가독성 최적).
// Pitfall: indicator.max 가 축마다 달라야 정상화 비교 가능 (단위 다른 값 정규화).
const radar = {
  title: { text: 'Radar', left: 'center', textStyle: { fontSize: 13 } },
  radar: {
    center: ['50%', '58%'],
    radius: '60%',
    indicator: [
      { name: 'Sales', max: 6500 },
      { name: 'Admin', max: 16000 },
      { name: 'IT', max: 30000 },
      { name: 'Support', max: 38000 },
      { name: 'Dev', max: 52000 }
    ]
  },
  series: [{ type: 'radar', data: [{ value: [4200, 3000, 20000, 35000, 50000], name: 'Budget' }] }]
}

/**
 * ============================================================================
 * ChartShowcase — ECharts 기본 차트 타입 카탈로그
 * ============================================================================
 *
 * 【역할】 echarts-for-react 로 4종 핵심 차트 option 객체를 시연.
 *         "어떤 option 조합으로 어떤 그림이 나오는지" 를 빠르게 확인.
 *
 * 【시연 항목】
 *   - Line  — `series.type='line'` + `smooth: true`
 *   - Bar   — `series.type='bar'`
 *   - Pie   — donut 형태 (`radius: ['35%', '65%']`)
 *   - Radar — `radar.indicator` 5축 + 단일 series
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/chart' }` (lazy)
 * 【포팅 원본】 자체 작성. 더 복잡한 데모는 `/templates/chart` (ChartTemplate)
 * 【의존성】 echarts-for-react, antd Card/Typography
 * 【참고】 https://echarts.apache.org/en/option.html
 */
export default function ChartShowcase() {
  const { t } = useLabel()
  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <div style={{ flex: '0 0 auto', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_CHART_COMPONENTS', 'Chart 컴포넌트')}
        </Title>
      </div>
      {/* 2x2 그리드 — 4종 차트 동시 표시 (반응형 비율 유지) */}
      {/* renderer: 'canvas' = 대용량 데이터 성능, 'svg' = 인쇄/벡터 export 용도 */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: 12
        }}
      >
        {/* Line — 시계열 추세 (요일별 KPI) */}
        <Card
          styles={{ body: { padding: 8, height: '100%' } }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <ReactECharts option={line} style={{ height: '100%' }} opts={{ renderer: 'canvas' }} />
        </Card>
        {/* Bar — 카테고리 비교 */}
        <Card
          styles={{ body: { padding: 8, height: '100%' } }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <ReactECharts option={bar} style={{ height: '100%' }} opts={{ renderer: 'canvas' }} />
        </Card>
        {/* Pie/Donut — 비율 표현 */}
        <Card
          styles={{ body: { padding: 8, height: '100%' } }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <ReactECharts option={pie} style={{ height: '100%' }} opts={{ renderer: 'canvas' }} />
        </Card>
        {/* Radar — 다차원 균형 */}
        <Card
          styles={{ body: { padding: 8, height: '100%' } }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <ReactECharts option={radar} style={{ height: '100%' }} opts={{ renderer: 'canvas' }} />
        </Card>
      </div>
    </PageContainer>
  )
}
